/* ----------------------------------------------------------------------------
 * "Repurpose for social" data source.
 *
 * POSTs a blog draft (title + body_md + optional SEO evidence) to
 * /v1/social/studio/repurpose, which generates ONE platform-appropriate social
 * post per platform (Facebook, Instagram, LinkedIn) via the AI Gateway,
 * deterministically hard-trims each to its platform limit, and LANDS one
 * bc_social_content_items DRAFT row per platform for human review.
 *
 * DRAFT-ONLY: nothing is published, scheduled or sent — the server creates
 * review_status='draft' rows only (connectors are gated, G-SOC-3). The caller
 * surfaces the returned drafts for the human to review/edit in Content Studio.
 * Workspace-scoped SERVER-SIDE (the caller never supplies tenancy); the request
 * is idempotent (the api client sends X-Bksesh-Idempotency-Key) so a retry
 * neither double-spends the AI budget nor duplicates draft rows.
 * -------------------------------------------------------------------------- */
import { apiRequest } from '@/lib/api';
import type { BlogDraftEvidence } from '@/data/copilot';

export type RepurposePlatform = 'facebook' | 'instagram' | 'linkedin';

/** One created social draft (a bc_social_content_items DRAFT row) + its trim metadata. */
export interface RepurposeDraft {
  id: number;
  platform: RepurposePlatform | string;
  caption: string;
  hashtags: string[];
  charCount: number;
  charLimit: number;
  charTarget: number;
  hashtagCount: number;
  hashtagLimit: number;
  reviewStatus: string;
  /** Content Studio editor deep-link for this draft. */
  editUrl: string;
}

export interface RepurposeResult {
  available: boolean;
  valid: boolean;
  /** 'connection_required' | 'insufficient_signal' | 'invalid_request' | undefined (success). */
  state?: string;
  message?: string;
  drafts: RepurposeDraft[];
  note?: string;
}

/** The raw API shape returned by RepurposeController::repurpose. */
interface RepurposeDto {
  available?: boolean;
  valid?: boolean;
  state?: string;
  message?: string;
  note?: string;
  drafts?: Array<Record<string, unknown>>;
}

function toDraft(d: Record<string, unknown>): RepurposeDraft {
  const hashtags = Array.isArray(d.hashtags)
    ? d.hashtags.filter((h): h is string => typeof h === 'string')
    : [];
  return {
    id: Number(d.id) || 0,
    platform: typeof d.platform === 'string' ? d.platform : typeof d.channel === 'string' ? d.channel : '',
    caption: typeof d.caption === 'string' ? d.caption : '',
    hashtags,
    charCount: Number(d.char_count) || 0,
    charLimit: Number(d.char_limit) || 0,
    charTarget: Number(d.char_target) || 0,
    hashtagCount: Number(d.hashtag_count) || hashtags.length,
    hashtagLimit: Number(d.hashtag_limit) || 0,
    reviewStatus: typeof d.review_status === 'string' ? d.review_status : 'draft',
    editUrl: typeof d.edit_url === 'string' ? d.edit_url : '',
  };
}

export interface RepurposeInput {
  /** The source blog draft id — used to derive a STABLE idempotency key so a
   *  retry after a lost response de-dupes server-side instead of creating a
   *  second set of drafts (IVG F3). */
  blogDraftId?: number;
  /** The ACTIVE "Viewing" domain (source blog's domain). Sent as the project_id
   *  query param so the server tags each created draft to that domain and it is
   *  visible in the domain-scoped Social library (resolved + re-validated
   *  server-side; a foreign id is rejected 422). Omit / null = unassigned. */
  projectId?: number | null;
  title: string;
  body_md: string;
  seo_evidence?: BlogDraftEvidence[];
}

/**
 * Repurpose a blog draft into per-platform social DRAFTS for review. Returns the
 * created drafts, or an honest unavailable/empty result (available/valid false)
 * with a message — never throws for those states. Network/HTTP failures reject
 * (ApiError) for the caller to surface.
 */
export async function repurposeBlogToSocial(input: RepurposeInput): Promise<RepurposeResult> {
  const body = {
    title: input.title ?? '',
    body_md: input.body_md ?? '',
    seo_evidence: Array.isArray(input.seo_evidence)
      ? input.seo_evidence.map((e) => ({ summary: e.summary, source_label: e.source_label }))
      : [],
    // Owner D7 (24-10-2026): the source blog draft id, so the server can link each
    // variant to its blog for the Content Library (validated server-side against
    // this workspace; ignored when unknown).
    ...(typeof input.blogDraftId === 'number' && input.blogDraftId > 0 ? { source_draft_id: input.blogDraftId } : {}),
  };
  // Stable idempotency key: the SAME blog draft repurposed twice (e.g. a retry
  // after a dropped response) de-dupes at the server replay cache rather than
  // minting a fresh UUID per click and creating duplicate draft rows (IVG F3).
  const idempotencyKey =
    typeof input.blogDraftId === 'number' && input.blogDraftId > 0
      ? `repurpose:${input.blogDraftId}`
      : undefined;
  // Thread the active domain as the project_id query param — the SAME shape the
  // content list uses — so the server tags the created drafts to that domain.
  const query =
    typeof input.projectId === 'number' && input.projectId > 0
      ? { project_id: input.projectId }
      : undefined;
  const res = await apiRequest<RepurposeDto>('/social/studio/repurpose', {
    method: 'POST',
    body,
    query,
    idempotencyKey,
  });
  return {
    available: res.available === true,
    valid: res.valid === true,
    state: typeof res.state === 'string' ? res.state : undefined,
    message: typeof res.message === 'string' ? res.message : undefined,
    note: typeof res.note === 'string' ? res.note : undefined,
    drafts: Array.isArray(res.drafts) ? res.drafts.map(toDraft) : [],
  };
}
